import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import PageHeader from '../../components/ui/PageHeader';
import { useTranslation } from '../../i18n/LanguageContext';
import { IconSearch, IconList } from '@tabler/icons-react';

export default function AuditLog() {
  const { t, language } = useTranslation();
  const { orgId } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDocs(query(
          collection(db, 'organisations', orgId, 'auditLog'),
          orderBy('createdAt', 'desc'),
          limit(200)
        ));
        setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const filtered = entries.filter(e => {
    const q = search.toLowerCase();
    return !q ||
      e.action?.toLowerCase().includes(q) ||
      e.performedByName?.toLowerCase().includes(q) ||
      e.targetName?.toLowerCase().includes(q);
  });

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader title={t('set.audit_log')} subtitle={t('set.audit_log_subtitle')} />
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <IconSearch size={14} stroke={1.5} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder={t('set.audit_search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <Card padding="0">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {['set.col_action', 'service.performed_by', 'checks.col_datetime'].map(h => (
              <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{t(h)}</div>
            ))}
          </div>
          {loading ? <LoadingSkeleton rows={8} /> : filtered.length === 0 ? (
            <EmptyState icon={<IconList size={28} stroke={1.5} />} title={t('set.no_audit')} message={search ? t('staff.adjust_search') : t('set.audit_empty_msg')} />
          ) : filtered.map((entry, i) => (
            <div key={entry.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr', padding: '10px 16px', borderBottom: i < filtered.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{entry.action || '—'}</div>
                {entry.targetName && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 1 }}>{entry.targetName}</div>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{entry.performedByName || '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{formatDateTime(entry.createdAt)}</div>
            </div>
          ))}
        </Card>
      </div>
    </Layout>
  );
}